import React, { useEffect } from 'react'
import { Box, FormControlLabel, FormHelperText } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { format } from 'date-fns'
import {
  EmtAutocomplete,
  EmtIcon,
  EmtRadio,
  EmtSelect,
  EmtTextarea,
  EmtTextField,
  EmtTypography,
  SelectOptionType,
  useMobile,
} from 'emt-components-v2'

import { Gender } from '@/dataV3/Common/Gender'
import { DatePickerField } from '@/components/PetSitter/EmtCalendar/DatePickerField'

import { useEMTState } from '@/store'
import { AnimalSpecies } from '@/dataV3/Animal/AnimalSpecies'
import { DogBreed } from '@/dataV3/Animal/DogBreed'
import { ConnectedUserAnimal } from '@/dataV3/Animal/ConnectedUserAnimal'

interface AnimalFormInputsProps {
  /** Current form values (e.g., name, breed, birthDate, etc.) */
  values: Record<string, any>

  /**
   * Callback to update a single field's value.
   * Receives the name of the field and the new value.
   */
  onFieldChange: (key: string, value: any) => void

  /** Validation errors keyed by field name (e.g., { name: 'Name required' }) */
  errors: Record<string, string>

  /**
   * Color/theme for highlights or borders.
   * Defaults to a purple shade if not provided.
   */
  color?: string

  /**
   * Flag to hide the name field.
   * Useful for certain animal types where a name is not required.
   */
  hideName?: boolean

  animal?: ConnectedUserAnimal
  defaultDescription?: string | null
}

const AnimalFormInputs: React.FC<AnimalFormInputsProps> = ({
  values,
  onFieldChange,
  errors,
  color = '#AE59C6',
  hideName,
  animal,
  defaultDescription = null,
}) => {
  const [isMobile] = useMobile()
  const { t } = useTranslation('connection')
  const { t: tc } = useTranslation('common')
  const { dogBreeds, catBreeds, nacBreeds } = useEMTState().configuration
  const { dogSizes } = useEMTState().configuration
  const isFirstRender = React.useRef(true)

  // Example animal options for the "radio" row
  const animalOptions = [
    { label: tc('dog'), iconClass: 'emt-dog', value: 'dog' },
    { label: tc('cat'), iconClass: 'emt-cat', value: 'cat' },
    { label: tc('other'), iconClass: 'emt-nac', value: 'nac' },
  ]

  const breedOptions: SelectOptionType[] = React.useMemo(() => {
    let breeds: DogBreed[]
    switch (values.selectedRadio as AnimalSpecies) {
      case AnimalSpecies.dog:
        breeds = dogBreeds
        break
      case AnimalSpecies.cat:
        breeds = catBreeds
        break
      case AnimalSpecies.nac:
        breeds = nacBreeds
        break
      default:
        breeds = []
    }

    return breeds
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((breed) => ({
        value: breed.slug,
        label: breed.name,
      }))
  }, [dogBreeds, catBreeds, nacBreeds, values.selectedRadio])

  const genderOptions: SelectOptionType[] = React.useMemo(
    () => [
      {
        value: Gender.male,
        label: tc('dogSex.male'),
      },
      {
        value: Gender.female,
        label: tc('dogSex.female'),
      },
    ],
    [],
  )

  const sterilizationOptions: SelectOptionType[] = React.useMemo(
    () => [
      { value: 'yes', label: tc('yes') },
      { value: 'no', label: tc('no') },
    ],
    [tc],
  )

  const sizeOptions: SelectOptionType[] = React.useMemo(
    () =>
      [...dogSizes]
        .sort((a, b) => a.position - b.position)
        .map((dogSize) => ({
          value: dogSize.id,
          label: dogSize.name + (dogSize.description ? ` ${dogSize.description}` : ''),
          description: dogSize.description,
        })),
    [dogSizes],
  )

  useEffect(() => {
    if (!values.selectedRadio) return

    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (animal && values.selectedRadio === animal?.speciesSlug) {
      onFieldChange('breedSlug', animal.breedSlug)
    } else {
      onFieldChange('breedSlug', '')
    }
  }, [values.selectedRadio])

  return (
    <>
      {/* Radio group for animal type */}
      <Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${animalOptions.length}, 1fr)`,
            gap: isMobile ? '2vw' : '20px',
          }}
        >
          {animalOptions.map((option, index) => (
            <FormControlLabel
              key={index}
              control={
                <EmtRadio
                  checked={values.selectedRadio === option.value}
                  color="greyGradient"
                  onChange={() => onFieldChange('selectedRadio', option.value)}
                />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <EmtIcon className={option.iconClass} size="small" />
                  <Box sx={{ marginLeft: '2px' }}>{option.label}</Box>
                </Box>
              }
              sx={{
                display: 'flex',
                height: isMobile ? '100px' : '115px',
                flexDirection: 'column-reverse',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '18px',
                background: '#fff',
                border: `1px solid ${errors.selectedRadio ? '#F42829' : '#E8E8EA'}`,
                textAlign: 'center',
                gap: '12px',
                cursor: 'pointer',
                margin: 0,
                '& p': {
                  display: 'flex',
                  alignItems: 'center',
                  height: '66px',
                },
                '& img': {
                  maxWidth: '100%',
                  height: '66px',
                  objectFit: 'contain',
                },
                '& .Mui-checked svg': {
                  borderColor: `${color}!important`,
                  backgroundColor: `${color}!important`,
                },
              }}
            />
          ))}
        </Box>
        {errors.selectedRadio && <FormHelperText error>{errors.selectedRadio}</FormHelperText>}
      </Box>

      {/* Text fields / selects */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Animal Name */}
        {!hideName && (
          <EmtTextField
            label={t('create-your-profile-petowner.animalFormInput.name')}
            value={values.displayName}
            onChange={(e) => onFieldChange('displayName', e.target.value)}
            error={!!errors.displayName}
            helperText={errors.displayName}
            inputProps={{ inputMode: 'text', pattern: "[A-Za-zÀ-ÿ\\s'-]*" }}
            maxLength={20}
          />
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr' },
            gap: '20px',
          }}
        >
          {/* Breed */}
          {breedOptions && values.selectedRadio && (
            <EmtAutocomplete
              // @ts-expect-error
              options={breedOptions}
              fullWidth
              label={
                values.selectedRadio === 'nac'
                  ? (t(`create-your-profile-petowner.animalFormInput.specie`) as string)
                  : (t('create-your-profile-petowner.animalFormInput.breed') as string)
              }
              onChange={(_, newValue) => {
                // @ts-expect-error
                onFieldChange('breedSlug', newValue?.value)
              }}
              // @ts-expect-error
              value={values.breedSlug ? breedOptions.find((breed) => breed.value === values.breedSlug) : null}
              error={!!errors.breedSlug}
              helperText={errors.breedSlug}
            />
          )}

          {/* Birth Date */}
          <DatePickerField
            label={t('create-your-profile-petowner.animalFormInput.birth')}
            value={values.birthDate.split('T')[0]}
            onChange={(birthDate) => onFieldChange('birthDate', birthDate)}
            error={!!errors.birthDate}
            helperText={errors.birthDate}
            max={format(new Date(), 'yyyy-MM-dd')}
            sx={{
              '& .MuiInputBase-root': {
                height: '56px',
              },
              '& input': {
                height: '24px',
                margin: 0,
              },
            }}
          />

          {/* Size */}
          <Box className="emtselect-item">
            <EmtSelect
              inputLabel={t('create-your-profile-petowner.animalFormInput.size') as string}
              options={sizeOptions}
              value={values.sizeSlug}
              onChange={(value) => onFieldChange('sizeSlug', value)}
              error={!!errors.sizeSlug}
              helperText={errors.sizeSlug}
            />
          </Box>

          {/* Gender */}
          <Box className="emtselect-item">
            <EmtSelect
              inputLabel={t('create-your-profile-petowner.animalFormInput.gender') as string}
              options={genderOptions}
              value={values.gender}
              onChange={(value) => onFieldChange('gender', value)}
              error={!!errors.gender}
              helperText={errors.gender}
            />
          </Box>

          {/* Sterilization - only for dogs and cats */}
          {(values.selectedRadio === 'dog' || values.selectedRadio === 'cat') && (
            <Box className="emtselect-item">
              <EmtSelect
                inputLabel={t('create-your-profile-petowner.animalFormInput.sterilized') as string}
                options={sterilizationOptions}
                value={values.sterilized === null ? null : values.sterilized ? 'yes' : 'no'}
                onChange={(value) => onFieldChange('sterilized', value === 'yes')}
                error={!!errors.sterilized}
                helperText={errors.sterilized}
              />
            </Box>
          )}
        </Box>

        {/* Description */}
        <Box>
          <EmtTypography variant="body1" fontWeight={700} sx={{ marginBottom: '5px' }}>
            {t('create-your-profile-petowner.animalFormInput.description') || 'Description'}
            {defaultDescription && (
              <span style={{ fontWeight: '400', fontSize: '14px', color: '#77757F' }}> (Optionnel)</span>
            )}
          </EmtTypography>
          <EmtTextarea
            hideLabelOnClick
            maxLength={1000}
            sx={{ height: '110px', paddingRight: '70px' }}
            label={t('create-your-profile-petowner.animalFormInput.descriptionLabel') as string}
            value={values.description}
            onChange={(value) => onFieldChange('description', value)}
            error={!!errors.description}
            helperText={errors.description}
          />
        </Box>
      </Box>
    </>
  )
}

export default AnimalFormInputs
